// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import type { SharedVaultKeyContribution } from '@cobault/key-agent';
import {
  api,
  SHARED_VAULT_LABEL_MAX_CHARS,
  type CreateSharedVaultRequest,
  type IssuedInvite,
  type SharedVaultRollingLimit,
} from '../api.js';
import {
  CLAIM_LINK_BEARER_WARNING,
  ClaimLinkCard,
  claimLinkSlotTitle,
} from '../components/claim-link-card.js';
import { parseBtcToSats } from '../format.js';
import { useSession } from '../session-context.js';
import {
  SHARED_VAULT_MAX_INVITEES_FALLBACK,
  useSharedVaultMaxInvitees,
  useSharedVaultPolicyEnabled,
} from '../spa-config.js';
import { SHARED_VAULT_TWO_STEP_NOTE } from '../shared-vault-accept-flow.js';
import { PlatformTermsDisclosure } from '../components/platform-terms-disclosure.js';
import { PLATFORM_TERMS_VERSION } from './platform-terms.js';
import { quorumLabel, formatRollingLimit } from './shared-vaults.js';
import '../styles/home.css';
import '../styles/accept.css';
import '../styles/shared-vaults.css';

// ── Pure helpers (exported for unit tests) ────────────────────────────

/** COB-543: one invitee slot as the wizard holds it — an optional cosmetic
 *  label the initiator types, plus a stable client-side id so a row keeps its
 *  identity (and its focused input) when a row above it is removed. The id is
 *  local to this component and never goes on the wire; the server mints the
 *  real `slot_id`.
 *
 *  A slot is NOT an invited person: there is no address here to validate, no
 *  duplicate to reject, and no self-invite to catch — the create response hands
 *  back one bearer claim link per slot and the initiator decides who gets which.
 *  The label is their own handle on that decision and nothing more. */
export interface InviteSlotDraft {
  id: string;
  label: string;
}

/** The label a slot contributes to the request: trimmed, with an empty or
 *  whitespace-only label becoming `null` (an unlabelled slot) — the same
 *  normalisation the backend applies at the boundary. The over-length case is
 *  kept out by the field's `maxLength`; a longer label reaching the server is a
 *  plain `400` rather than something silently truncated here. */
export function normalizeSlotLabel(label: string): string | null {
  const trimmed = label.trim();
  return trimmed === '' ? null : trimmed;
}

/** Roster size `n` = the invitee slots plus the initiator's own slot. */
export function participantCount(inviteCount: number): number {
  return inviteCount + 1;
}

/** The minimum quorum `m` a vault may have. A **policy** vault folds the
 *  additive verifier key `V` into the descriptor, so AR-2 requires `m ≥ 2` —
 *  the verifier can never move funds on its own (always needs ≥1 participant
 *  alongside it). A no-policy vault allows `m ≥ 1`. The argument is whether the
 *  vault has an allowlist policy (≥1 address) — there is no separate toggle; the
 *  presence of an allowlist *is* the policy switch. */
export function minQuorum(hasAllowlist: boolean): number {
  return hasAllowlist ? 2 : 1;
}

/** Selectable quorum thresholds `minM…n` for a roster of size `n`. */
export function quorumOptions(n: number, minM = 1): number[] {
  const lo = Math.max(1, minM);
  return Array.from({ length: Math.max(0, n - lo + 1) }, (_, i) => i + lo);
}

/** Default `m` for a roster of `n`: the canonical 2-of-n (matching the
 *  yield product's 2-of-3 mental model), or 1-of-1 for a lone roster. */
export function defaultQuorum(n: number): number {
  return Math.min(2, n);
}

/** Clamp a quorum into `[minM, n]` — the R-INV-1 `m ≤ n` guard (and the AR-2
 *  `m ≥ 2` floor for a policy vault), applied as the roster or the policy
 *  toggle changes so the selector can never offer an `m` the create call would
 *  reject. */
export function clampQuorum(m: number, n: number, minM = 1): number {
  return Math.max(Math.max(1, minM), Math.min(m, n));
}

// ── AR-21 / R-INV-1c: the zero-redundancy `m == n` shape ──────────────
//
// `m == n` is a *valid* descriptor — R-INV-1 only requires `m ≤ n` — and stays
// creatable. It is nonetheless zero-redundancy, and `descriptor-spec.md`
// §R-INV-1c requires the SPA to warn against it and disclose the freeze risk
// **before funding**:
//
//   * **no-policy `m == n`** — one lost key freezes the vault permanently. There
//     is no verifier key `V` to lower the human threshold and (OQ4 deferred) no
//     CLTV self-recovery leaf. One lost seed = funds unspendable forever.
//   * **policy `m == n`** — asymmetric: `m-1` surviving humans **+ V** still
//     reach quorum, so it survives exactly ONE lost key, and only for a
//     withdrawal the verifier will co-sign (allow-listed). A second loss is
//     terminal. This is the one shape where `V` buys liveness, not just policy.
//
// Two surfaces, both driven by the pure helpers below: an inline warning under
// the quorum selector, and an interstitial the initiator passes through on
// *every* Continue while `m == n`. The interstitial is deliberately **not**
// session-suppressed — create is the last cheap moment to change the threshold;
// afterwards it is sealed into the descriptor and immutable.

/** Whether the chosen quorum is the zero-redundancy `m == n` shape (AR-21).
 *
 *  `n < 2` is excluded on purpose: a freshly-opened wizard with no invitees is
 *  trivially `1-of-1`, and warning about that — before the user has typed
 *  anything, about a vault `canCreateSharedVault` refuses to create anyway —
 *  would be noise rather than protection. */
export function isZeroRedundancyQuorum(quorumM: number, n: number): boolean {
  return n >= 2 && quorumM === n;
}

/** Whether the `m == n` interstitial can offer the one-click weaker quorum.
 *  It cannot when `n - 1` falls below the vault's floor — a **policy** vault
 *  with exactly two participants, where AR-2 pins `m ≥ 2`, so `2-of-2` is the
 *  only shape on offer and redundancy needs a third co-signer, not a lower
 *  `m`. */
export function canWeakenQuorum(n: number, minM: number): boolean {
  return n - 1 >= Math.max(1, minM);
}

/** The inline warning rendered under the quorum selector while `m == n`. The
 *  two variants are materially different outcomes, not tone: no-policy freezes
 *  on the first loss; policy survives exactly one, allow-listed only. */
export function zeroRedundancyHint(n: number, hasAllowlist: boolean): string {
  if (hasAllowlist) {
    return (
      `Your full user quorum: unrestricted withdrawals need all ${n} ` +
      `participant keys. If one key is lost, the remaining participants plus ` +
      `the verifier can still reach quorum — but only for withdrawals to ` +
      `allow-listed addresses. If more than one key is lost, the vault ` +
      `contents are permanently irrecoverable.`
    );
  }
  return (
    `All ${n} keys are required — zero redundancy. If any single key is lost, ` +
    `quorum can never be formed again and the vault contents are permanently ` +
    `irrecoverable. Consider requiring ${n - 1} of ${n} instead.`
  );
}

/** The interstitial's title, per vault flavour.
 *
 *  The trigger is the same for both — `m == n` over the **human** roster — but a
 *  policy vault folds the verifier key `V` into the descriptor, so the same
 *  trigger describes a different shape: `n` of `n+1`, not `n` of `n`. The title
 *  must therefore not read as a plain "2 of 2" when the very next step (confirm)
 *  calls the same vault 2-of-3; naming the verifier keeps the two surfaces
 *  telling one story. */
export function zeroRedundancyPromptTitle(hasAllowlist: boolean): string {
  return hasAllowlist ? 'Full user quorum + verifier' : 'Full user quorum';
}

/** The interstitial's body copy. When the weaker quorum is unreachable
 *  (`canWeaken === false` — only ever the policy 2-participant floor, so `n` is
 *  invariably 2 there, since a no-policy vault with `n ≥ 2` can always drop to
 *  `n-1 ≥ 1`) the "we recommend `n-1` of `n+1`" sentence is replaced, not
 *  followed, by the only remedy that exists: another co-signer. Recommending a
 *  threshold the floor forbids would contradict the very next sentence.
 *
 *  Both policy variants state the shape as `n` of `n+1` — the key total the
 *  confirm step and the vault detail page also show — and say plainly what the
 *  surviving `n-1` + `V` quorum can and cannot do: allow-listed withdrawals the
 *  verifier approves, never an unrestricted one. */
export function zeroRedundancyPromptBody(
  n: number,
  hasAllowlist: boolean,
  canWeaken: boolean,
): string {
  if (!canWeaken) {
    return (
      `With the verifier control, this vault is now ${n} of ${n + 1}: both ` +
      `participants together can withdraw to any address. If one key is ` +
      `lost, the remaining participant plus the verifier can still reach ` +
      `quorum — but only for withdrawals the verifier approves, to ` +
      `allow-listed addresses (and up to transaction limits, if set). If ` +
      `more than one key is lost, the vault contents are permanently ` +
      `irrecoverable. Add another co-signer to gain more redundancy.`
    );
  }
  return hasAllowlist
    ? `With the verifier control, this vault is now ${n} of ${n + 1}: all ` +
        `${n} participants together can withdraw to any address. If one ` +
        `participant's key is lost, the remaining ${n - 1} plus the verifier ` +
        `can still reach quorum — but only for withdrawals the verifier ` +
        `approves, to allow-listed addresses (and up to transaction limits, ` +
        `if set). If more than one key is lost, the vault contents are ` +
        `permanently irrecoverable. We recommend a quorum of at most ` +
        `${n - 1} of ${n + 1} so unrestricted withdrawals survive a lost key.`
    : `You've required your full user quorum — all ${n} participants must ` +
        `sign every withdrawal. If any one key is lost or a co-signer becomes ` +
        `unresponsive, quorum can never be formed and the vault contents are ` +
        `permanently irrecoverable. We recommend a quorum of at most ` +
        `${n - 1} of ${n} so the vault survives a lost key.`;
}

/** Strip whitespace and formatting hyphens from a pasted address — the same
 *  normalisation the personal wallet's send field applies (`wallet.tsx`
 *  `sanitizeAddress`), so the allowlist accepts an address copied with stray
 *  spaces. The backend re-validates + canonicalises via `cobault-btc-address`. */
export function sanitizeAllowlistAddress(raw: string): string {
  return raw.replace(/[\s\-\u00AD\u200B]/g, '');
}

/** Network → bech32 HRP prefix. Mirrors `wallet.tsx`'s `BECH32_PREFIXES`
 *  (copied, not shared — per the estate's code-quality rule). */
const BECH32_PREFIXES: Record<string, string> = {
  bitcoin: 'bc1',
  signet: 'tb1',
  testnet: 'tb1',
  regtest: 'bcrt1',
};

/** A best-effort wrong-network warning for a bech32 address — the same prefix
 *  heuristic the wallet send field uses. Returns a message when the address's
 *  HRP clearly belongs to a *different* network than the vault's, else `null`.
 *  This is a hint, not a parser: the backend's `cobault-btc-address` is the
 *  authority and re-checks the network regardless. */
export function allowlistNetworkMismatchHint(
  address: string,
  network: string | null,
): string | null {
  if (!network) return null;
  const expected = BECH32_PREFIXES[network];
  if (!expected) return null;
  const lower = address.toLowerCase();
  if (lower.startsWith(expected)) return null;
  for (const [net, prefix] of Object.entries(BECH32_PREFIXES)) {
    if (net !== network && lower.startsWith(prefix)) {
      return `This looks like a ${net} address (${prefix}…) but this vault is on ${network} (${expected}…).`;
    }
  }
  return null;
}

/** Validate a candidate allowlist entry against the current list + network.
 *  Returns an error message, or `null` if it may be added. Keeps obviously-bad
 *  or wrong-network addresses + duplicates out of the list before submit; the
 *  backend remains the authority on canonical validity. */
export function allowlistAddError(
  raw: string,
  existing: string[],
  network: string | null,
): string | null {
  const addr = sanitizeAllowlistAddress(raw);
  if (addr === '') return 'Enter a destination address.';
  const mismatch = allowlistNetworkMismatchHint(addr, network);
  if (mismatch) return mismatch;
  if (existing.includes(addr)) return `${addr} is already on the allowlist.`;
  return null;
}

/** COB-453 (SV-1): the allowlist the request and every derived control must use.
 *  While the operator toggle is off the policy builder is inert ("coming soon")
 *  and MUST contribute nothing — no `policy` on the wire, no `m ≥ 2` floor, no
 *  rolling limit — so the create is byte-identical to the p2p path. Typed
 *  addresses are kept in component state (not discarded) so a mid-session flip
 *  back on does not lose the user's work; this is the single place that decides
 *  whether they count. The server independently 403s a policy create while the
 *  toggle is off — this is presentation, not the gate. */
export function effectiveAllowlist(
  allowlist: string[],
  policyEnabled: boolean,
): string[] {
  return policyEnabled ? allowlist : [];
}

/** Whether a policy vault is being configured: at least one allowlist address
 *  has been added. There is no separate enable toggle — the presence of an
 *  address *is* the policy. An empty allowlist is a plain no-policy m-of-n
 *  vault (the verifier is not folded into the descriptor). */
export function hasAllowlistPolicy(allowlist: string[]): boolean {
  return allowlist.length > 0;
}

/** COB-456: the hint under the slot list once the invitee cap is reached, and
 *  the reason the "Add co-owner" button stops. One source of truth for both,
 *  since the same sentence has to answer "why can't I add another?" in either
 *  place. COB-543: counts co-owner *slots*, not typed addresses. */
export function inviteCapHint(cap: number): string {
  return `Up to ${cap} co-owner slot${cap === 1 ? '' : 's'} can be added to one vault.`;
}

/** Whether the current setup is a valid create request: at least one invitee
 *  slot, no more than `maxInvitees` of them (COB-456), `minM ≤ m ≤ n`, and —
 *  when an allowlist policy is present (≥1 address) — `m ≥ 2` (AR-2, the
 *  verifier always needs a participant alongside it). An empty allowlist is a
 *  valid no-policy vault.
 *
 *  COB-543: slot count, quorum and policy are the whole of it. There is no
 *  address-validity arm any more — a slot carries an optional cosmetic label
 *  and nothing that could be wrong.
 *
 *  `maxInvitees` defaults to the permissive consensus bound rather than the
 *  product default so a caller that has no live config (and every pre-COB-456
 *  test) behaves exactly as before; the server's own 400 is the real cap. */
export function canCreateSharedVault(
  invites: readonly { label: string }[],
  quorumM: number,
  allowlist: string[] = [],
  maxInvitees: number = SHARED_VAULT_MAX_INVITEES_FALLBACK,
): boolean {
  if (invites.length < 1) return false;
  if (invites.length > maxInvitees) return false;
  const n = participantCount(invites.length);
  if (quorumM < minQuorum(hasAllowlistPolicy(allowlist)) || quorumM > n) return false;
  return true;
}

/** Assemble the `POST /shared-vault` body from the wizard state + the
 *  key-agent's PoP contribution (the initiator's own slot). A non-empty
 *  `allowlist` adds the Phase-2 `policy` (a policy vault); an empty/omitted one
 *  leaves the body byte-identical to the no-policy Phase-1 path. An optional
 *  rolling `limit` (Phase 3 — COB-188/237) rides the allowlist policy: it is
 *  attached **only** when an allowlist is present (a limit-only policy is
 *  refused server-side), so a no-policy create stays byte-identical. */
export function buildCreateSharedVaultRequest(
  vaultId: string,
  slotId: string,
  quorumM: number,
  invites: readonly { label: string }[],
  contribution: SharedVaultKeyContribution,
  allowlist: string[] = [],
  limit: SharedVaultRollingLimit | null = null,
): CreateSharedVaultRequest {
  const body: CreateSharedVaultRequest = {
    vault_id: vaultId,
    initiator_slot_id: slotId,
    quorum_m: quorumM,
    // COB-543: one entry per slot, in row order — the create response returns
    // its claim links in the same order, so the initiator's labels line up with
    // the links they are about to hand out.
    invites: invites.map((slot) => ({ label: normalizeSlotLabel(slot.label) })),
    initiator_multisig_pubkey_hex: contribution.multisig_pubkey_hex,
    initiator_derivation_index: contribution.derivation_index,
    // HW.13a: the account key-origin for the registration record (software /
    // Web3Auth populate it; the hardware backend leaves it undefined until HW.13b).
    initiator_key_origin: contribution.account_key_origin,
    // WS-H v2 proof (HW.8): software emits `der-sha256-v1`, hardware
    // `bsm-bip137-v1`; the server accepts either on the `*_proof` surface.
    initiator_pop_proof: contribution.pop_proof,
    // COB-512: the version of the Platform Terms the confirm step rendered,
    // from the published page's own constant — see the accept flow's note.
    platform_terms_version: PLATFORM_TERMS_VERSION,
  };
  if (allowlist.length > 0) {
    body.policy = limit ? { allowlist, limit } : { allowlist };
  }
  return body;
}

// ── B3.1 rolling-limit helpers (Phase 3 — COB-188/238) ────────────────

/** Bitcoin's `MAX_MONEY` in satoshis — the upper bound on a limit amount,
 *  mirroring the backend schema's `MAX_MONEY` (copied, not shared, per the
 *  estate's code-quality rule). */
export const LIMIT_MAX_MONEY_SATS = 2_100_000_000_000_000;

/** The selectable rolling-limit windows. `custom` ⇒ a whole number of days. */
export type LimitPeriod = 'hour' | 'day' | 'week' | 'custom';

/** Map a period (+ a custom day count) to the contract's `window_secs`.
 *  Returns `null` for an invalid custom day count (non-integer, ≤ 0) so the
 *  caller fails closed rather than sending a zero/garbage window. */
export function windowSecondsForPeriod(
  period: LimitPeriod,
  customDays?: number,
): number | null {
  switch (period) {
    case 'hour':
      return 3600;
    case 'day':
      return 86_400;
    case 'week':
      return 604_800;
    case 'custom': {
      if (customDays === undefined || !Number.isInteger(customDays) || customDays < 1) {
        return null;
      }
      return customDays * 86_400;
    }
  }
}

/** Parse a BTC-denominated limit amount to satoshis, enforcing the contract
 *  bounds (`0 < amount_sats ≤ MAX_MONEY`). Returns `null` for empty/malformed/
 *  out-of-range input; the backend re-checks the same bounds. */
export function parseLimitAmountSats(btcInput: string): number | null {
  const sats = parseBtcToSats(btcInput);
  if (!Number.isFinite(sats) || sats <= 0 || sats > LIMIT_MAX_MONEY_SATS) {
    return null;
  }
  return sats;
}

/** Assemble a `SharedVaultRollingLimit` from the wizard's limit state, or
 *  `null` when no limit applies / any field is invalid (so the create call
 *  simply omits the limit rather than sending a malformed one).
 *
 *  Two structural gates, both fail-closed:
 *  - **No allowlist ⇒ no limit, ever.** A limit-only policy is refused
 *    server-side (verifier `NoAllowlist`, COB-241), so an empty allowlist
 *    returns `null` *even with a valid amount* — a limit-only body is
 *    structurally impossible to assemble here.
 *  - The limit is **field-driven**: an empty amount means the user simply has
 *    not set a limit (it is optional), so that too returns `null`. */
export function buildRollingLimit(
  hasAllowlist: boolean,
  amountBtcInput: string,
  period: LimitPeriod,
  customDays?: number,
): SharedVaultRollingLimit | null {
  if (!hasAllowlist) return null;
  if (amountBtcInput.trim() === '') return null;
  const amount_sats = parseLimitAmountSats(amountBtcInput);
  const window_secs = windowSecondsForPeriod(period, customDays);
  if (amount_sats === null || window_secs === null) return null;
  return { amount_sats, window_secs };
}

/** A user-facing validation message for the limit inputs, or `null` when there
 *  is nothing to validate (no allowlist, or no limit amount entered) or the
 *  limit is fully valid. Drives the inline error + the Continue gate. An empty
 *  amount is *not* an error — the limit is optional; it only validates once the
 *  user has both an allowlist and a typed amount. */
export function limitConfigError(
  hasAllowlist: boolean,
  amountBtcInput: string,
  period: LimitPeriod,
  customDays?: number,
): string | null {
  if (!hasAllowlist) return null;
  if (amountBtcInput.trim() === '') return null;
  if (parseLimitAmountSats(amountBtcInput) === null) {
    return 'Enter a limit amount in BTC (greater than 0).';
  }
  if (windowSecondsForPeriod(period, customDays) === null) {
    return 'Enter a whole number of days (1 or more) for the limit window.';
  }
  return null;
}

// ── Page ───────────────────────────────────────────────────────────────

/** COB-543: `links` is a terminal step, not a way-station. The create response
 *  is the one and only time the raw claim links exist anywhere the SPA can read
 *  them, so the wizard must NOT route away on success — it hands them over and
 *  lets the initiator leave under their own steam. */
type Step = 'setup' | 'confirm' | 'creating' | 'links';

/** A fresh, empty invitee slot. `crypto.randomUUID` is already required by this
 *  page (the vault + slot ids at create), so the row key costs nothing new. */
function blankInviteSlot(): InviteSlotDraft {
  return { id: crypto.randomUUID(), label: '' };
}

export function SharedVaultCreate() {
  const { route } = useLocation();
  const session = useSession();

  // Logged-in users only — and, since COB-504, that is the whole gate.
  //
  // This page used to copy the yield accept flow's KYC redirect, but the two
  // are not the same act. A shared vault is a peer-to-peer `m`-of-`n`
  // multisig between its own participants: Cobault takes no deposit, offers
  // no contract, and is not a counterparty to it (on a no-policy vault it
  // holds no key at all). Accordingly the backend has NO KYC gate on any
  // `/shared-vault*` route — the sole server-side one is strategy
  // `create_vault` (lib.rs, F3) — so this redirect was refusing a page the
  // server would have served, and blocking pre-KYC users out of the one
  // product surface that never needed verification.
  //
  // `pages/accept.tsx` deliberately keeps its redirect: that one mirrors a
  // refusal the server really makes.
  useEffect(() => {
    if (!session.isLoggedIn) route('/');
  }, [session.isLoggedIn, route]);

  const [step, setStep] = useState<Step>('setup');
  // COB-543: invitee *slots*, not addresses. The wizard opens with one — a
  // shared vault needs at least one co-owner besides the initiator, so an empty
  // list is never a state the user wants to be in, and the label on it is
  // optional so the row is immediately valid.
  const [invites, setInvites] = useState<InviteSlotDraft[]>(() => [
    blankInviteSlot(),
  ]);
  // COB-543: the minted claim links, held only for the `links` step. This is
  // the sole copy — the server keeps a hash and nothing else — so it lives in
  // state until the user navigates away, and is never re-fetchable.
  const [issued, setIssued] = useState<IssuedInvite[]>([]);
  const [createdVaultId, setCreatedVaultId] = useState<string | null>(null);
  const [quorumM, setQuorumM] = useState(1);
  const [quorumTouched, setQuorumTouched] = useState(false);
  // AR-21 / R-INV-1c: the zero-redundancy interstitial, raised by Continue while
  // `m == n`. Not session-suppressed — it fires on every such Continue.
  const [zeroRedundancyPrompt, setZeroRedundancyPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase-2 allowlist policy (COB-216). Empty ⇒ a no-policy vault (the unchanged
  // Phase-1 path). ≥1 address ⇒ a policy vault co-signed by the verifier. There
  // is no separate enable toggle: the presence of an address *is* the policy
  // switch (COB-242).
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [addrInput, setAddrInput] = useState('');
  // Phase-3 rolling limit (COB-188/238). Rides the allowlist policy — only
  // meaningful when an allowlist is set (a limit-only policy is refused server-
  // side). The limit is field-driven too: a typed amount activates it; the
  // inputs are disabled (not hidden) until ≥1 address exists, and the typed
  // values survive removing the last address so the user does not lose work.
  const [limitAmount, setLimitAmount] = useState('');
  const [limitPeriod, setLimitPeriod] = useState<LimitPeriod>('day');
  const [limitCustomDays, setLimitCustomDays] = useState('7');
  // COB-453 (SV-1): the operator toggle. `true` (the server default and the
  // fallback when /api/spa-config is unreachable) leaves this page unchanged;
  // `false` renders the verifier-controls card as "coming soon" and drops the
  // policy from the request entirely.
  const policyEnabled = useSharedVaultPolicyEnabled();
  // COB-456: the live per-vault invitee cap. Presentation only — `POST
  // /shared-vault` re-reads the same runtime-config key and 400s on its own —
  // but stopping at the cap here is the difference between a friendly "that's
  // the limit" and a user assembling 30 addresses only to be refused.
  const maxInvitees = useSharedVaultMaxInvitees();

  const n = participantCount(invites.length);
  const inviteCapReached = invites.length >= maxInvitees;
  const activeAllowlist = effectiveAllowlist(allowlist, policyEnabled);
  const hasAllowlist = hasAllowlistPolicy(activeAllowlist);
  const minM = minQuorum(hasAllowlist);

  // Keep the threshold sane as the roster or the allowlist presence changes:
  // clamp a user-chosen `m` into `[minM, n]`, or track the 2-of-n default while
  // the user hasn't picked. Adding the first allowlist address raises the floor
  // to AR-2's `m ≥ 2`.
  useEffect(() => {
    setQuorumM((m) =>
      quorumTouched ? clampQuorum(m, n, minM) : Math.max(defaultQuorum(n), minM),
    );
  }, [n, quorumTouched, minM]);

  /** COB-543: append one co-owner slot. The cap is re-checked here as well as
   *  on the button, so a stale render can never add the (cap + 1)th row. */
  function addInvite() {
    setInvites((cur) => {
      if (cur.length >= maxInvitees) return cur;
      return [...cur, blankInviteSlot()];
    });
    setError(null);
  }

  function setInviteLabel(id: string, label: string) {
    setInvites((cur) =>
      cur.map((slot) => (slot.id === id ? { ...slot, label } : slot)),
    );
    setError(null);
  }

  function removeInvite(id: string) {
    setInvites((cur) => cur.filter((slot) => slot.id !== id));
    setError(null);
  }

  function chooseQuorum(m: number) {
    setQuorumTouched(true);
    setQuorumM(m);
  }

  /** Setup → confirm. AR-21: a zero-redundancy `m == n` roster is intercepted
   *  by the interstitial first; every other threshold advances directly. */
  function handleContinue() {
    if (isZeroRedundancyQuorum(quorumM, n)) {
      setZeroRedundancyPrompt(true);
      return;
    }
    setStep('confirm');
  }

  /** Add the typed address(es) to the allowlist. Mirrors `commitEmails`:
   *  sanitised, network-checked, deduped; the backend re-validates. */
  function commitAddresses() {
    const raw = addrInput.trim();
    if (!raw) return;
    const next = allowlist.slice();
    let firstError: string | null = null;
    for (const token of raw.split(/[\s,]+/).filter(Boolean)) {
      const err = allowlistAddError(token, next, session.bitcoinNetwork);
      if (err) {
        firstError ??= err;
        continue;
      }
      next.push(sanitizeAllowlistAddress(token));
    }
    setAllowlist(next);
    setAddrInput('');
    setError(firstError);
  }

  function removeAddress(addr: string) {
    setAllowlist((cur) => cur.filter((a) => a !== addr));
    setError(null);
  }

  // The limit only applies when an allowlist policy is present (it rides it) and
  // an amount has been typed; both `buildRollingLimit` and `limitConfigError`
  // enforce that gate, returning null/no-error for an empty allowlist.
  const customDays = limitPeriod === 'custom' ? Number(limitCustomDays) : undefined;
  const limitError = limitConfigError(hasAllowlist, limitAmount, limitPeriod, customDays);
  const effectiveLimit = buildRollingLimit(
    hasAllowlist,
    limitAmount,
    limitPeriod,
    customDays,
  );
  const canContinue =
    canCreateSharedVault(invites, quorumM, activeAllowlist, maxInvitees) &&
    limitError === null;

  async function handleCreate() {
    if (!session.agent) {
      setError('Session expired. Please log in again.');
      return;
    }
    if (
      !canCreateSharedVault(invites, quorumM, activeAllowlist, maxInvitees) ||
      limitError !== null
    ) {
      setStep('setup');
      return;
    }
    setStep('creating');
    setError(null);
    try {
      // Client-generated ids: the PoP binds them, so they must exist before
      // the row (api-contract §2/§3). A retry mints fresh ids + a fresh key.
      const vaultId = crypto.randomUUID();
      const slotId = crypto.randomUUID();
      const contribution = await session.agent.contributeSharedVaultKey(
        vaultId,
        slotId,
      );
      const res = await api.createSharedVault(
        buildCreateSharedVaultRequest(
          vaultId,
          slotId,
          quorumM,
          invites,
          contribution,
          activeAllowlist,
          effectiveLimit,
        ),
      );
      // COB-543: DO NOT navigate. `res.invites` carries the raw bearer claim
      // links, and this response is the only place they will ever exist — the
      // server stored their hashes alone. Routing to the vault page here would
      // silently throw away every link the initiator now has to distribute.
      setIssued(res.invites);
      setCreatedVaultId(res.vault_id);
      setStep('links');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the shared vault.');
      setStep('confirm');
    }
  }

  if (!session.isLoggedIn) return null;

  return (
    <div class="accept-page">
      {step === 'setup' && (
        <SetupStep
          invites={invites}
          addInvite={addInvite}
          setInviteLabel={setInviteLabel}
          removeInvite={removeInvite}
          maxInvitees={maxInvitees}
          inviteCapReached={inviteCapReached}
          n={n}
          quorumM={quorumM}
          minM={minM}
          chooseQuorum={chooseQuorum}
          hasAllowlist={hasAllowlist}
          policyEnabled={policyEnabled}
          allowlist={activeAllowlist}
          addrInput={addrInput}
          setAddrInput={(v) => {
            setAddrInput(v);
            setError(null);
          }}
          commitAddresses={commitAddresses}
          removeAddress={removeAddress}
          limitAmount={limitAmount}
          setLimitAmount={(v) => {
            setLimitAmount(v);
            setError(null);
          }}
          limitPeriod={limitPeriod}
          setLimitPeriod={setLimitPeriod}
          limitCustomDays={limitCustomDays}
          setLimitCustomDays={(v) => {
            setLimitCustomDays(v);
            setError(null);
          }}
          limitError={limitError}
          error={error}
          canContinue={canContinue}
          onBack={() => route('/shared-vaults')}
          onNext={handleContinue}
        />
      )}

      {step === 'setup' && zeroRedundancyPrompt && (
        <ZeroRedundancyQuorumModal
          n={n}
          minM={minM}
          hasAllowlist={hasAllowlist}
          onWeaken={() => {
            chooseQuorum(n - 1);
            setZeroRedundancyPrompt(false);
            setStep('confirm');
          }}
          onKeep={() => {
            setZeroRedundancyPrompt(false);
            setStep('confirm');
          }}
          onBack={() => setZeroRedundancyPrompt(false)}
        />
      )}

      {step === 'confirm' && (
        <ConfirmStep
          invites={invites}
          n={n}
          quorumM={quorumM}
          allowlist={activeAllowlist}
          limit={effectiveLimit}
          error={error}
          onBack={() => setStep('setup')}
          onCreate={handleCreate}
        />
      )}

      {step === 'creating' && (
        <div class="accept-loading" role="status" aria-live="polite">
          <div class="accept-spinner" aria-hidden="true" />
          <p>Creating your shared vault…</p>
          <p class="accept-loading-sub">
            Generating your key and preparing claim links — this takes a few
            seconds.
          </p>
        </div>
      )}

      {step === 'links' && createdVaultId && (
        <ClaimLinksStep
          invites={issued}
          onDone={() => route(`/shared-vault/${createdVaultId}`)}
        />
      )}
    </div>
  );
}

// ── Step 3: the claim links (COB-543) ─────────────────────────────────

/** The hand-over screen. Terminal by construction: it is mounted only after a
 *  successful create, and it is the ONE place the raw claim links are ever
 *  legible — the server keeps their SHA-256 and nothing else, so anything that
 *  navigates away from here destroys them for good.
 *
 *  That is why the copy leads with the two facts and their remedy before the
 *  cards, rather than tucking a caveat under them: a reader who scans the links,
 *  copies one and closes the tab must already have been told that the rest are
 *  gone and how to mint replacements. Exported for render tests. */
export function ClaimLinksStep({
  invites,
  onDone,
}: {
  invites: IssuedInvite[];
  onDone: () => void;
}) {
  return (
    <div data-testid="sv-claim-links-step">
      <h2 class="accept-title">Your vault is created — here are the links</h2>
      <p class="accept-subtitle">
        Send one claim link to each co-owner. Opening a link is how they consent
        to participate and contribute their signing key.
      </p>
      <p class="accept-error sv-link-warning" data-testid="sv-claim-link-warning">
        {CLAIM_LINK_BEARER_WARNING}
      </p>

      <div class="sv-links-list">
        {invites.map((invite, i) => (
          <ClaimLinkCard
            key={invite.slot_id}
            invite={invite}
            title={claimLinkSlotTitle(invite, i)}
          />
        ))}
      </div>

      <div class="accept-actions">
        <button type="button" class="btn btn-primary" onClick={onDone}>
          Done — go to vault
        </button>
      </div>
    </div>
  );
}

// ── Step 1: invitees + quorum ─────────────────────────────────────────

interface SetupStepProps {
  invites: InviteSlotDraft[];
  addInvite: () => void;
  setInviteLabel: (id: string, label: string) => void;
  removeInvite: (id: string) => void;
  /** COB-456: the live per-vault invitee cap (invitee slots only). */
  maxInvitees: number;
  /** COB-456: `invites.length >= maxInvitees` — "Add co-owner" is disabled. */
  inviteCapReached: boolean;
  n: number;
  quorumM: number;
  minM: number;
  chooseQuorum: (m: number) => void;
  hasAllowlist: boolean;
  /** COB-453 (SV-1): false ⇒ the verifier-controls card renders "coming soon". */
  policyEnabled: boolean;
  allowlist: string[];
  addrInput: string;
  setAddrInput: (v: string) => void;
  commitAddresses: () => void;
  removeAddress: (addr: string) => void;
  limitAmount: string;
  setLimitAmount: (v: string) => void;
  limitPeriod: LimitPeriod;
  setLimitPeriod: (p: LimitPeriod) => void;
  limitCustomDays: string;
  setLimitCustomDays: (v: string) => void;
  limitError: string | null;
  error: string | null;
  canContinue: boolean;
  onBack: () => void;
  onNext: () => void;
}

function SetupStep({
  invites,
  addInvite,
  setInviteLabel,
  removeInvite,
  maxInvitees,
  inviteCapReached,
  n,
  quorumM,
  minM,
  chooseQuorum,
  hasAllowlist,
  policyEnabled,
  allowlist,
  addrInput,
  setAddrInput,
  commitAddresses,
  removeAddress,
  limitAmount,
  setLimitAmount,
  limitPeriod,
  setLimitPeriod,
  limitCustomDays,
  setLimitCustomDays,
  limitError,
  error,
  canContinue,
  onBack,
  onNext,
}: SetupStepProps) {
  return (
    <div>
      <h2 class="accept-title">New shared vault</h2>
      <p class="accept-subtitle">
        Add a slot for each co-signer and choose how many signatures move funds.
        You'll get a single-use claim link per slot to share directly with the
        person you intend. You're a co-owner too, so once every participant has
        consented you'll Sign to activate the vault alongside everyone else.
      </p>
      <p class="accept-subtitle">{SHARED_VAULT_TWO_STEP_NOTE}</p>

      {/* COB-543: one row per co-owner slot. There is no address to type —
          creating the vault mints a single-use claim link per slot and hands
          the whole set back on the next-but-one screen for the initiator to
          distribute. The label is optional and purely their own reminder of
          which link went to whom; it is never authz-bearing, so nothing here
          validates it beyond the server's length cap. */}
      <div class="accept-field">
        <span class="sv-field-label">Co-owners</span>
        {invites.length > 0 && (
          <ul class="sv-invite-slots">
            {invites.map((slot, i) => (
              <li key={slot.id} class="sv-invite-slot">
                <input
                  class="accept-input sv-invite-slot-label"
                  type="text"
                  maxLength={SHARED_VAULT_LABEL_MAX_CHARS}
                  placeholder="Label (optional) — e.g. Alice"
                  aria-label={`Label for co-owner ${i + 1} (optional)`}
                  value={slot.label}
                  onInput={(e) =>
                    setInviteLabel(slot.id, (e.target as HTMLInputElement).value)
                  }
                />
                <button
                  type="button"
                  class="sv-chip-remove"
                  aria-label={`Remove co-owner ${i + 1}`}
                  onClick={() => removeInvite(slot.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* COB-456: at the cap the button is genuinely disabled and the hint
            says why in plain words, instead of letting the user keep adding
            rows into a create the server will refuse. */}
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          data-testid="sv-add-invite"
          disabled={inviteCapReached}
          onClick={addInvite}
        >
          + Add co-owner
        </button>
        <p class="accept-input-hint" data-testid="sv-invite-cap-hint">
          {inviteCapReached
            ? inviteCapHint(maxInvitees)
            : "Add one slot per co-owner. You'll get a single-use claim link for each — a label just names the slot for everyone on the vault, and never controls who can claim it."}
        </p>
      </div>

      <div class="accept-field">
        <span class="sv-field-label">Signatures required</span>
        <div
          class="sv-quorum-options"
          role="group"
          aria-label="Signatures required to move funds"
        >
          {quorumOptions(n, minM).map((m) => (
            <button
              key={m}
              type="button"
              class={`sv-quorum-option${m === quorumM ? ' sv-quorum-option--selected' : ''}`}
              aria-pressed={m === quorumM}
              onClick={() => chooseQuorum(m)}
            >
              {m}
            </button>
          ))}
        </div>
        <QuorumHint quorumM={quorumM} n={n} hasAllowlist={hasAllowlist} />
      </div>

      <PolicySection
        hasAllowlist={hasAllowlist}
        policyEnabled={policyEnabled}
        allowlist={allowlist}
        addrInput={addrInput}
        setAddrInput={setAddrInput}
        commitAddresses={commitAddresses}
        removeAddress={removeAddress}
        limitAmount={limitAmount}
        setLimitAmount={setLimitAmount}
        limitPeriod={limitPeriod}
        setLimitPeriod={setLimitPeriod}
        limitCustomDays={limitCustomDays}
        setLimitCustomDays={setLimitCustomDays}
        limitError={limitError}
      />

      {error && <p class="accept-error">{error}</p>}

      <div class="accept-actions">
        <button type="button" class="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          class="btn btn-primary"
          onClick={onNext}
          disabled={!canContinue}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

// ── Quorum hint + the AR-21 zero-redundancy warning ───────────────────

/** The line under the quorum selector: what the chosen `m` means in plain
 *  words, plus — while `m == n` — the R-INV-1c zero-redundancy warning. The
 *  warning **augments** the hint rather than replacing it, so the user keeps
 *  the plain reading of their own choice alongside the consequence. Exported so
 *  the copy can be render-tested without mounting the whole wizard. */
export function QuorumHint({
  quorumM,
  n,
  hasAllowlist,
}: {
  quorumM: number;
  n: number;
  hasAllowlist: boolean;
}) {
  return (
    <>
      <p class="accept-input-hint">
        {hasAllowlist ? (
          <>
            <strong>{quorumLabel(quorumM, n + 1)}</strong> — any {quorumM} of
            the {n + 1} keys ({n} participant{n === 1 ? '' : 's'} + verifier)
            must sign to move funds.
          </>
        ) : (
          <>
            <strong>{quorumLabel(quorumM, n)}</strong> — any {quorumM} of the{' '}
            {n} participant{n === 1 ? '' : 's'} must sign to move funds.
          </>
        )}
      </p>
      {isZeroRedundancyQuorum(quorumM, n) && (
        <p
          class="accept-input-hint sv-quorum-warn"
          data-testid="sv-quorum-zero-redundancy"
        >
          {zeroRedundancyHint(n, hasAllowlist)}
        </p>
      )}
    </>
  );
}

// ── AR-21 interstitial: "Full user quorum [+ verifier]" ───────────────

/** The `m == n` interstitial, raised on every setup→confirm Continue while the
 *  roster is zero-redundancy. Mirrors `ActivationSigningModal`'s chrome
 *  (overlay + panel + title/desc + right-aligned secondary-then-primary
 *  actions); no signing eyebrow, because nothing is being signed here.
 *
 *  The buttons count **keys**, not participants: a policy vault's descriptor
 *  holds `n+1` of them, so the same `m == n` roster reads "Keep 2 of 3" here
 *  and 2-of-3 on the confirm step, rather than disagreeing with it.
 *
 *  The primary action is the *safer* one: weaken to `n-1` and continue. Where
 *  weakening is impossible (policy vault, exactly two participants — AR-2 pins
 *  `m ≥ 2`) the weaken button is omitted entirely rather than offered and
 *  rejected, and the primary becomes "Go back" so the default gesture returns
 *  to setup where a third co-signer can be added. Dismissing the overlay never
 *  advances the wizard. */
export function ZeroRedundancyQuorumModal({
  n,
  minM,
  hasAllowlist,
  onWeaken,
  onKeep,
  onBack,
}: {
  n: number;
  minM: number;
  hasAllowlist: boolean;
  /** Continue to confirm at `n-1` of `n`. */
  onWeaken: () => void;
  /** Continue to confirm at `n` of `n`, as configured. */
  onKeep: () => void;
  /** Dismiss and return to setup, unchanged. */
  onBack: () => void;
}) {
  const weakenable = canWeakenQuorum(n, minM);
  const totalKeys = hasAllowlist ? n + 1 : n;
  return (
    <div class="shared-vault-modal-overlay" onClick={onBack}>
      <div
        class="shared-vault-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="sv-zero-redundancy-modal"
      >
        <h2 class="shared-vault-modal-title">
          {zeroRedundancyPromptTitle(hasAllowlist)}
        </h2>
        <p class="shared-vault-modal-desc">
          {zeroRedundancyPromptBody(n, hasAllowlist, weakenable)}
        </p>
        <div class="shared-vault-modal-actions">
          <button type="button" class="btn btn-secondary" onClick={onKeep}>
            Keep {n} of {totalKeys}
          </button>
          {weakenable ? (
            <button type="button" class="btn btn-primary" onClick={onWeaken}>
              Use {n - 1} of {totalKeys}
            </button>
          ) : (
            <button type="button" class="btn btn-primary" onClick={onBack}>
              Go back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Verifier-controls card (optional policy) ──────────────────────────
//
// One card holding both verifier controls — an address allowlist and a rolling
// spending limit — visible together (COB-242). There are no enable checkboxes:
// activation is field-driven. Adding ≥1 allowlist address turns the verifier
// co-signer on; typing a limit amount turns the limit on. The limit inputs are
// disabled (not hidden) until an allowlist address exists, because a limit-only
// policy is refused server-side (verifier `NoAllowlist`, COB-241) — disabling
// makes that dependency legible rather than surprising. An empty card is a
// plain no-policy m-of-n vault.
//
// COB-453 (SV-1): with the operator toggle off the whole card is inert — every
// input `disabled`, a "Coming soon" badge on the heading, and the explanatory
// copy swapped for a future-tense line. Greyed out, deliberately NOT hidden:
// the controls are shipped and the soft launch is telling users they are next,
// not pretending they never existed. This is presentation only; the server 403s
// a policy create independently (`SHARED_VAULT_POLICY_ENABLED`).

export interface PolicySectionProps {
  hasAllowlist: boolean;
  /** COB-453 (SV-1): false ⇒ inert "coming soon" card. */
  policyEnabled: boolean;
  allowlist: string[];
  addrInput: string;
  setAddrInput: (v: string) => void;
  commitAddresses: () => void;
  removeAddress: (addr: string) => void;
  limitAmount: string;
  setLimitAmount: (v: string) => void;
  limitPeriod: LimitPeriod;
  setLimitPeriod: (p: LimitPeriod) => void;
  limitCustomDays: string;
  setLimitCustomDays: (v: string) => void;
  limitError: string | null;
}

export function PolicySection({
  hasAllowlist,
  policyEnabled,
  allowlist,
  addrInput,
  setAddrInput,
  commitAddresses,
  removeAddress,
  limitAmount,
  setLimitAmount,
  limitPeriod,
  setLimitPeriod,
  limitCustomDays,
  setLimitCustomDays,
  limitError,
}: PolicySectionProps) {
  return (
    <div
      class={`accept-field sv-verifier-card${policyEnabled ? '' : ' sv-verifier-card--soon'}`}
      aria-disabled={policyEnabled ? undefined : 'true'}
      data-testid="sv-verifier-card"
    >
      <span class="sv-field-label">
        Verifier controls (optional)
        {!policyEnabled && (
          <>
            {' '}
            <span class="sv-coming-soon-badge">Coming soon</span>
          </>
        )}
      </span>
      <p class="accept-input-hint">
        {policyEnabled ? (
          <>
            Add the Cobault verifier as an extra co-signer that will only help
            send to addresses you allow, optionally within a spending limit. It
            can never move funds on its own, and your participants can still
            send anywhere if enough of them sign without it. Leave this empty
            for a standard multisig vault with no verifier.
          </>
        ) : (
          <>
            Soon you'll be able to add the Cobault verifier as an extra
            co-signer that only helps send to addresses you allow, optionally
            within a spending limit. These controls aren't available yet — your
            vault will be a standard multisig vault with no verifier.
          </>
        )}
      </p>

      <div class="sv-policy-field">
        <span class="sv-field-label">Allowlist addresses</span>
        <div class="sv-chip-field">
          {allowlist.length > 0 && (
            <ul class="sv-chips">
              {allowlist.map((addr) => (
                <li key={addr} class="sv-chip">
                  <span class="sv-chip-email mono">{addr}</span>
                  <button
                    type="button"
                    class="sv-chip-remove"
                    aria-label={`Remove ${addr}`}
                    onClick={() => removeAddress(addr)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            id="sv-allowlist-input"
            class="accept-input sv-chip-input"
            type="text"
            placeholder="Allowed destination address"
            value={addrInput}
            disabled={!policyEnabled}
            onInput={(e) => setAddrInput((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                commitAddresses();
              }
            }}
            onBlur={commitAddresses}
          />
        </div>
        <p class="accept-input-hint">
          {policyEnabled ? (
            <>
              Press Enter to add each address. Adding one turns the verifier on:
              it co-signs only to these destinations, and at least{' '}
              <strong>2 signatures</strong> are then required so the verifier
              always needs a participant alongside it (it can lower the
              participant threshold by one, never to zero).
            </>
          ) : (
            <em class="sv-hint-muted">
              Allowlisted destinations are coming soon.
            </em>
          )}
        </p>
      </div>

      <div class="sv-policy-field sv-policy-limit">
        <span class="sv-field-label">Set spending limit</span>
        <p class="accept-input-hint">
          Cap how much the verifier will help move over a rolling window. This
          is fixed for the life of the vault and can't be changed later. Your
          participants can still move more by signing without the verifier.
          {!hasAllowlist && (
            <>
              {' '}
              <em class="sv-hint-muted">
                {policyEnabled
                  ? 'Allow at least one address to activate.'
                  : 'Spending limits are coming soon.'}
              </em>
            </>
          )}
        </p>

        <div class="sv-limit-body">
          <div class="sv-limit-row">
            <label class="sv-field-label" for="sv-limit-amount">
              Amount (BTC)
            </label>
            <input
              id="sv-limit-amount"
              class="accept-input"
              type="text"
              inputMode="decimal"
              placeholder="0.05"
              value={limitAmount}
              disabled={!policyEnabled || !hasAllowlist}
              onInput={(e) =>
                setLimitAmount((e.target as HTMLInputElement).value)
              }
            />
          </div>
          <div class="sv-limit-row">
            <label class="sv-field-label" for="sv-limit-period">
              Per
            </label>
            <select
              id="sv-limit-period"
              class="accept-input"
              value={limitPeriod}
              disabled={!policyEnabled || !hasAllowlist}
              onChange={(e) =>
                setLimitPeriod(
                  (e.target as HTMLSelectElement).value as LimitPeriod,
                )
              }
            >
              <option value="hour">hour</option>
              <option value="day">day</option>
              <option value="week">week</option>
              <option value="custom">custom (days)</option>
            </select>
          </div>
          {limitPeriod === 'custom' && (
            <div class="sv-limit-row">
              <label class="sv-field-label" for="sv-limit-days">
                Days
              </label>
              <input
                id="sv-limit-days"
                class="accept-input"
                type="number"
                min="1"
                step="1"
                value={limitCustomDays}
                disabled={!policyEnabled || !hasAllowlist}
                onInput={(e) =>
                  setLimitCustomDays((e.target as HTMLInputElement).value)
                }
              />
            </div>
          )}
          {limitError && <p class="accept-error">{limitError}</p>}
        </div>
      </div>
    </div>
  );
}

// ── Step 2: confirm ───────────────────────────────────────────────────

interface ConfirmStepProps {
  invites: InviteSlotDraft[];
  n: number;
  quorumM: number;
  allowlist: string[];
  limit: SharedVaultRollingLimit | null;
  error: string | null;
  onBack: () => void;
  onCreate: () => void;
}

function ConfirmStep({
  invites,
  n,
  quorumM,
  allowlist,
  limit,
  error,
  onBack,
  onCreate,
}: ConfirmStepProps) {
  // A policy vault folds the additive verifier key into the sealed descriptor,
  // so the keyset is `n` humans + 1 verifier at threshold `quorumM` — count it
  // in the signatures total and name it beside the human participants, without
  // ever implying it is a human or required to spend (R-INV-1).
  const hasVerifier = hasAllowlistPolicy(allowlist);
  return (
    <div>
      <h2 class="accept-title">Confirm</h2>
      <dl class="confirm-card">
        <div class="confirm-row">
          <dt>Participants</dt>
          <dd class="tabular-nums">
            {n} (you + {invites.length} invitee{invites.length === 1 ? '' : 's'})
            {hasVerifier ? ' + verifier' : ''}
          </dd>
        </div>
        <div class="confirm-row">
          <dt>Signatures required</dt>
          <dd class="tabular-nums">
            {quorumLabel(quorumM, hasVerifier ? n + 1 : n)}
          </dd>
        </div>
        <div class="confirm-row">
          <dt>Spending policy</dt>
          <dd class="tabular-nums">
            {allowlist.length > 0
              ? `Verifier allowlist (${allowlist.length} address${allowlist.length === 1 ? '' : 'es'})`
              : 'None'}
          </dd>
        </div>
        {limit && (
          <div class="confirm-row">
            <dt>Rolling limit</dt>
            <dd class="tabular-nums">{formatRollingLimit(limit)}</dd>
          </div>
        )}
      </dl>

      {/* COB-543: the slots as configured. An unlabelled slot is named by its
          position — there is no address to fall back on, and its `slot_id`
          does not exist yet. */}
      <ul class="sv-confirm-invites">
        {invites.map((slot, i) => (
          <li key={slot.id} class="sv-confirm-invite">
            {normalizeSlotLabel(slot.label) ?? `Unlabelled slot ${i + 1}`}
          </li>
        ))}
      </ul>

      {allowlist.length > 0 && (
        <ul class="sv-confirm-allowlist">
          {allowlist.map((addr) => (
            <li key={addr} class="sv-confirm-invite mono">
              {addr}
            </li>
          ))}
        </ul>
      )}

      <p class="confirm-risk-summary">
        You'll get a single-use claim link for each slot — share each one
        directly with the person you intend. Opening a link is how a co-owner
        consents to participation. Once every participant has consented,
        everyone signs to activate the vault — confirming its setup, not any
        legal document — and only then does the vault activate and receive its
        deposit address.
      </p>

      {/* COB-512: the creator derives a per-vault key and funds the vault, so
          the same terms + risk disclosure and the same recorded acceptance
          apply here as on the accept path. Last thing before the button. */}
      <PlatformTermsDisclosure surface="create" />

      {error && <p class="accept-error">{error}</p>}

      <div class="accept-actions">
        <button type="button" class="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" class="btn btn-primary" onClick={onCreate}>
          Create shared vault
        </button>
      </div>
    </div>
  );
}
